import React, { useState } from 'react';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  CurrencyRupeeIcon,
  StarIcon,
  QuestionMarkCircleIcon,
  EnvelopeIcon,
  PuzzlePieceIcon,
  ChevronLeftIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface CMSNavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  description: string;
}

const cmsNavigation: CMSNavigationItem[] = [
  { 
    name: 'Pricing Plans', 
    href: '/super-admin/cms/pricing', 
    icon: CurrencyRupeeIcon,
    description: 'Manage subscription plans and pricing'
  },
  { 
    name: 'Product Features', 
    href: '/super-admin/cms/features', 
    icon: StarIcon,
    description: 'Manage product features and benefits'
  },
  { 
    name: 'Testimonials', 
    href: '/super-admin/cms/testimonials', 
    icon: StarIcon,
    description: 'Manage customer testimonials and reviews'
  },
  { 
    name: 'FAQs', 
    href: '/super-admin/cms/faqs', 
    icon: QuestionMarkCircleIcon,
    description: 'Manage frequently asked questions'
  },
  { 
    name: 'Contact Submissions', 
    href: '/super-admin/cms/contact', 
    icon: EnvelopeIcon,
    description: 'Manage contact form submissions'
  },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export default function CMSLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user, isSuperAdmin } = useAuth();

  if (!user || !isSuperAdmin()) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Narrow sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 bg-gray-800">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <Link 
                  to="/super-admin/dashboard"
                  className="flex items-center text-gray-300 hover:text-white"
                >
                  <ChevronLeftIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">Back to Admin</span>
                </Link>
              </div>
              <div className="flex items-center flex-shrink-0 px-4 mt-4">
                <PuzzlePieceIcon className="h-8 w-8 text-white mr-3" />
                <h1 className="text-white text-lg font-semibold">Content Management</h1>
              </div>
              <nav className="mt-5 flex-1 px-2 space-y-1">
                {cmsNavigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={classNames(
                        isActive
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                        'group flex items-center px-2 py-2 text-sm font-medium rounded-md'
                      )}
                    >
                      <item.icon
                        className={classNames(
                          isActive ? 'text-gray-300' : 'text-gray-400 group-hover:text-gray-300',
                          'mr-3 flex-shrink-0 h-6 w-6'
                        )}
                        aria-hidden="true"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{item.name}</div>
                        <div className="text-xs text-gray-400 group-hover:text-gray-300">
                          {item.description}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sidebar */}
      <div className="md:hidden">
        {sidebarOpen && (
          <div className="fixed inset-0 flex z-40">
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
            <div className="relative flex-1 flex flex-col max-w-xs w-full bg-gray-800">
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  onClick={() => setSidebarOpen(false)}
                >
                  <XMarkIcon className="h-6 w-6 text-white" />
                </button>
              </div>
              <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                <div className="flex-shrink-0 flex items-center px-4">
                  <Link 
                    to="/super-admin/dashboard"
                    className="flex items-center text-gray-300 hover:text-white"
                  >
                    <ChevronLeftIcon className="h-5 w-5 mr-2" />
                    <span className="text-sm font-medium">Back to Admin</span>
                  </Link>
                </div>
                <div className="flex items-center flex-shrink-0 px-4 mt-4">
                  <PuzzlePieceIcon className="h-8 w-8 text-white mr-3" />
                  <h1 className="text-white text-lg font-semibold">Content Management</h1>
                </div>
                <nav className="mt-5 px-2 space-y-1">
                  {cmsNavigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={classNames(
                          isActive
                            ? 'bg-gray-900 text-white'
                            : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                          'group flex items-center px-2 py-2 text-base font-medium rounded-md'
                        )}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <item.icon className="mr-4 h-6 w-6" />
                        <div className="flex-1">
                          <div className="text-base font-medium">{item.name}</div>
                          <div className="text-sm text-gray-400 group-hover:text-gray-300">
                            {item.description}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-hidden">
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
          <button
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex">
              <div className="w-full flex md:ml-0">
                <div className="relative w-full text-gray-400 focus-within:text-gray-600">
                  <div className="flex items-center h-16">
                    <h2 className="text-2xl font-bold text-gray-900">
                      Content Management System
                    </h2>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
